import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Joyride, ACTIONS, EVENTS, STATUS } from "react-joyride";
import { listSystems } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * AudRI guided product tour — react-joyride driven walkthrough that covers the
 * full happy path: Dashboard KPIs → open a system → audit provenance →
 * findings / CAPA → inspection-ready PDF → 21 CFR Part 11 e-signature.
 *
 * The tour spans two routes (/dashboard and /systems/:id) so stepIndex is
 * controlled and the step that transitions between them performs the
 * navigation before advancing the index.
 */

const TOUR_FLAG = "audri_tour_seen_v2";
const TourContext = createContext(null);

export const useTour = () => {
    const ctx = useContext(TourContext);
    if (!ctx) throw new Error("useTour must be used inside <TourProvider>");
    return ctx;
};

const joyrideStyles = {
    tooltip: {
        borderRadius: 4,
        border: "1px solid #e5e5e5",
        padding: 20,
        fontFamily: "'IBM Plex Sans', sans-serif",
    },
    tooltipTitle: {
        fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif",
        fontSize: 16,
        letterSpacing: "-0.01em",
        marginBottom: 6,
    },
    tooltipContent: {
        fontSize: 13,
        lineHeight: 1.55,
        color: "#404040",
    },
    buttonPrimary: {
        backgroundColor: "#C2410C",
        color: "#ffffff",
        borderRadius: 2,
        fontSize: 12,
        padding: "8px 14px",
    },
    buttonBack: {
        color: "#525252",
        fontSize: 12,
    },
    buttonSkip: {
        color: "#737373",
        fontSize: 12,
    },
    buttonClose: { display: "none" },
};

const joyrideOptions = {
    primaryColor: "#C2410C",
    textColor: "#0a0a0a",
    backgroundColor: "#ffffff",
    arrowColor: "#ffffff",
    overlayColor: "rgba(0, 0, 0, 0.55)",
    zIndex: 10000,
    showProgress: true,
    spotlightPadding: 6,
    buttons: ["back", "skip", "primary"],
    overlayClickAction: "none",
    skipBeacon: true,
    targetWaitTimeout: 4000,
    scrollOffset: 120,
    scrollDuration: 250,
};

export function TourProvider({ children }) {
    const { isAuthed } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [targetSystemId, setTargetSystemId] = useState(null);
    const [targetSystemName, setTargetSystemName] = useState(null);

    // Resolve the demo system we'll walk through (prefer LIMS-PROD-15 from the
    // seeded demo tenant, otherwise fall back to the first system available).
    const resolveTargetSystem = useCallback(async () => {
        try {
            const rows = await listSystems();
            if (!rows?.length) return null;
            const preferred =
                rows.find((s) => s.name === "LIMS-PROD-15") ||
                rows.find((s) => (s.name || "").startsWith("LIMS")) ||
                rows[0];
            setTargetSystemId(preferred.id);
            setTargetSystemName(preferred.name);
            return preferred;
        } catch (_e) {
            return null;
        }
    }, []);

    const start = useCallback(async () => {
        const sys = await resolveTargetSystem();
        if (!sys) {
            // Not enough data to run a useful tour — user needs to seed first.
            // Fall back: start on dashboard and let the tour highlight the seed button.
        }
        if (location.pathname !== "/dashboard") {
            navigate("/dashboard");
            // Give the dashboard a moment to mount before joyride paints.
            setTimeout(() => {
                setStepIndex(0);
                setRun(true);
            }, 500);
        } else {
            setStepIndex(0);
            setRun(true);
        }
    }, [location.pathname, navigate, resolveTargetSystem]);

    const stop = useCallback((markSeen = true) => {
        setRun(false);
        setStepIndex(0);
        if (markSeen) {
            try {
                localStorage.setItem(TOUR_FLAG, "1");
            } catch (_e) {
                /* ignore */
            }
        }
    }, []);

    // First-login auto-start: only on /dashboard, only once per browser.
    useEffect(() => {
        if (!isAuthed) return;
        if (location.pathname !== "/dashboard") return;
        let seen = "0";
        try {
            seen = localStorage.getItem(TOUR_FLAG) || "0";
        } catch (_e) {
            seen = "0";
        }
        if (seen === "1") return;
        // Wait for Dashboard KPIs to paint before triggering.
        const t = setTimeout(() => start(), 1400);
        return () => clearTimeout(t);
    }, [isAuthed, location.pathname, start]);

    const steps = useMemo(() => {
        const sysName = targetSystemName || "LIMS-PROD-15";
        return [
            {
                target: "body",
                placement: "center",
                disableBeacon: true,
                title: "Welcome to AudRI for GxP",
                content:
                    "A guided walkthrough of the GxP audit workflow — from raw SOPs and validation packages, through per-finding QA approvals, to an inspection-ready Part 11–signed report.",
            },
            {
                target: '[data-testid="kpi-systems"]',
                title: "Fleet-wide KPIs",
                content:
                    "Every GxP system under your org's oversight shows up here — counts, overdue reviews, open findings.",
            },
            {
                target: '[data-testid="kpi-compliance"]',
                title: "Rolling compliance score",
                content:
                    "Weighted average of each system's latest periodic review. Drops instantly when a new critical finding is generated.",
            },
            {
                target: '[data-testid="btn-seed-demo"]',
                title: "One-click demo data",
                content:
                    "We've already seeded 15 realistic GxP systems for this demo tenant (LIMS, MES, EDMS, SCADA, ERP-GxP…).",
            },
            {
                target: `[data-testid="system-row-${sysName}"]`,
                title: `Drill into ${sysName}`,
                content:
                    "Click any row to open the system's audit workspace. We'll do it for you — hit Next.",
            },
            // --- on /systems/:id ---
            {
                target: '[data-testid="stat-compliance"]',
                title: "System-level compliance",
                content:
                    "Computed by Claude Haiku 4.5 from rule extraction, evidence matching and traceability coverage — no spreadsheet archaeology.",
            },
            {
                target: '[data-testid="reviewer-status"]',
                title: "Reproducible by construction",
                content:
                    "Every audit stores model, prompt version, temperature, seed and SHA-256 of every input document — so a regulator can replay it bit-for-bit.",
            },
            {
                target: '[data-testid="tab-findings"]',
                title: "Findings & CAPA",
                content:
                    "Severity-classified findings (Critical / Major / Minor / Observation) with AI-drafted CAPA. We'll open this tab next so you can see the per-finding QA workflow.",
            },
            // --- on findings tab (auto-clicked by handleCallback) ---
            {
                target: '[data-testid^="review-status-pill-"]',
                title: "Review status, per finding",
                content:
                    "Every finding carries its own review state — Draft (just generated), Pending Review (auditor edited), or Approved (QA-signed). QA managers triage at the finding level, not just per-audit.",
            },
            {
                target: '[data-testid^="btn-edit-finding-"]',
                title: "Auditor-side edits",
                content:
                    "QA Auditors can adjust severity, status, title, description or CAPA inline. Every change is logged in an immutable audit trail (who, when, from → to) — and any prior signature is invalidated.",
            },
            {
                target: '[data-testid^="btn-approve-finding-"]',
                title: "Per-finding 21 CFR Part 11 e-signature",
                content:
                    "QA Managers re-enter their password to bind a SHA-256 signature to the exact finding state. Tamper-evident, regulator-defensible, and surgical — sign one finding at a time, not just the whole audit.",
            },
            {
                target: '[data-testid="btn-export"]',
                title: "Inspection-ready PDF",
                content:
                    "One click, fully formatted for FDA 483 / EU Annex 11 inspections. Includes full provenance header and signature block.",
            },
            {
                target: '[data-testid="btn-sign-audit"]',
                title: "Audit-level e-signature",
                content:
                    "Once every finding is reviewed, the QA Manager seals the whole audit with a second Part 11 signature — the binding inspection record.",
            },
            {
                target: "body",
                placement: "center",
                title: "That's AudRI for GxP.",
                content:
                    "120-hour manual audits, reduced to ~4–8 hours — with a per-finding QA workflow that mirrors how your team actually reviews. Take the tour again anytime from the sidebar.",
            },
        ];
    }, [targetSystemName]);

    const LAST_DASHBOARD_STEP = 4; // index of the "Drill into SYSNAME" step
    const FINDINGS_TAB_STEP = 7; // index of "Findings & CAPA" — auto-click the tab on Next

    const handleCallback = useCallback(
        (data) => {
            const { action, index, status, type, lifecycle } = data;
            // eslint-disable-next-line no-console
            console.log(
                "[tour]",
                JSON.stringify({ type, action, index, status, lifecycle }),
            );

            if (
                status === STATUS.FINISHED ||
                status === STATUS.SKIPPED ||
                action === ACTIONS.CLOSE
            ) {
                stop(true);
                return;
            }

            if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
                // User clicked Next / Back OR the step target is missing.
                if (action === ACTIONS.PREV) {
                    const nextIdx = Math.max(0, index - 1);
                    // If we're crossing back from system detail to dashboard
                    if (index === LAST_DASHBOARD_STEP + 1) {
                        navigate("/dashboard");
                        setTimeout(() => setStepIndex(LAST_DASHBOARD_STEP), 400);
                    } else {
                        setStepIndex(nextIdx);
                    }
                    return;
                }

                // Forward navigation
                const nextIdx = index + 1;
                if (index === LAST_DASHBOARD_STEP && targetSystemId) {
                    // Navigate to the system detail page and advance stepIndex
                    // in one frame. Keep run=true so joyride doesn't reset to
                    // the beacon lifecycle on the new step.
                    navigate(`/systems/${targetSystemId}`);
                    setStepIndex(nextIdx);
                    return;
                }
                if (index === FINDINGS_TAB_STEP) {
                    // Switch to the Findings & CAPA tab via URL query param —
                    // SystemDetail is wired to read ?tab=findings and update
                    // its controlled Tabs state. Plain DOM click() doesn't
                    // work here because Radix Tabs is uncontrolled internally.
                    if (targetSystemId) {
                        navigate(`/systems/${targetSystemId}?tab=findings`, {
                            replace: true,
                        });
                    }
                    // Wait for the findings TabsContent to render before
                    // joyride looks for the next spotlight target.
                    setTimeout(() => setStepIndex(nextIdx), 600);
                    return;
                }
                if (type === EVENTS.TARGET_NOT_FOUND) {
                    // Skip past missing targets so the tour doesn't stall.
                    setStepIndex(nextIdx);
                    return;
                }
                setStepIndex(nextIdx);
            }
        },
        [navigate, stop, targetSystemId],
    );

    const value = useMemo(() => ({ start, stop, isActive: run }), [run, start, stop]);

    return (
        <TourContext.Provider value={value}>
            {children}
            <Joyride
                steps={steps}
                run={run}
                stepIndex={stepIndex}
                continuous
                scrollToFirstStep
                options={joyrideOptions}
                locale={{
                    back: "Back",
                    close: "Close",
                    last: "Finish",
                    next: "Next",
                    skip: "Skip tour",
                }}
                styles={joyrideStyles}
                onEvent={handleCallback}
            />
        </TourContext.Provider>
    );
}
